import { useMaskEditorDataStore } from '@/stores/maskEditorDataStore'
import { useMaskEditorStore } from '@/stores/maskEditorStore'
import { useNodeOutputStore } from '@/stores/nodeOutputStore'
import type {
  EditorOutputData,
  EditorOutputLayer,
  ImageRef
} from '@/stores/maskEditorDataStore'
import { isCloud } from '@/platform/distribution/types'
import { api } from '@/scripts/api'
import { app } from '@/scripts/app'
import { createAnnotatedPath } from '@/utils/createAnnotatedPath'
import { parseFilePath } from '@/utils/formatUtil'
import { encodeRgbaPng } from '@/utils/pngRgba'
import type { LGraphNode } from '@/lib/litegraph/src/litegraph'

function imageRefFromUploadResponse(
  data: { name?: string; subfolder?: string; type?: string },
  fallback: ImageRef
): ImageRef {
  if (!data?.name) return fallback

  const uploadedPath = data.subfolder
    ? `${data.subfolder}/${data.name}`
    : data.name
  const parsed = parseFilePath(uploadedPath)

  return {
    filename: parsed.filename,
    subfolder: parsed.subfolder || fallback.subfolder,
    type: data.type || fallback.type
  }
}

export function useMaskEditorSaver() {
  const dataStore = useMaskEditorDataStore()
  const editorStore = useMaskEditorStore()
  const nodeOutputStore = useNodeOutputStore()

  const save = async (): Promise<void> => {
    const sourceNode = dataStore.sourceNode as LGraphNode
    if (!sourceNode || !dataStore.inputData) {
      throw new Error('No source node or input data')
    }

    try {
      const outputData = await prepareOutputData()
      dataStore.outputData = outputData

      await updateNodePreview(sourceNode, outputData)

      outputData.image.ref = await uploadImage(outputData.image)

      updateNodeWithServerReferences(sourceNode, outputData)

      app.canvas.setDirty(true)
    } catch (error) {
      console.error('[MaskEditorSaver] Save failed:', error)
      throw error
    }
  }

  async function prepareOutputData(): Promise<EditorOutputData> {
    const maskCanvas = editorStore.maskCanvas
    const paintCanvas = editorStore.rgbCanvas
    const imgCanvas = editorStore.imgCanvas

    if (!maskCanvas || !paintCanvas || !imgCanvas) {
      throw new Error('Canvas not initialized')
    }

    return {
      image: createSingleAlphaImage(
        imgCanvas,
        paintCanvas,
        maskCanvas,
        `clipspace-mask-${Date.now()}.png`
      )
    }
  }

  function createSingleAlphaImage(
    imgCanvas: HTMLCanvasElement,
    paintCanvas: HTMLCanvasElement,
    maskCanvas: HTMLCanvasElement,
    filename: string
  ): EditorOutputLayer {
    const canvas = document.createElement('canvas')
    canvas.width = imgCanvas.width
    canvas.height = imgCanvas.height
    const ctx = canvas.getContext('2d')!

    ctx.drawImage(imgCanvas, 0, 0)
    ctx.globalCompositeOperation = 'source-over'
    ctx.drawImage(paintCanvas, 0, 0)

    const maskCtx = maskCanvas.getContext('2d')!
    const maskData = maskCtx.getImageData(
      0,
      0,
      maskCanvas.width,
      maskCanvas.height
    )

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    for (let i = 0; i < imageData.data.length; i += 4) {
      imageData.data[i + 3] = 255 - maskData.data[i + 3]
    }
    ctx.putImageData(imageData, 0, 0)

    const blob = encodeRgbaPng(imageData)
    const ref = createFileRef(filename)

    return { canvas, blob, ref }
  }

  async function uploadImage(layer: EditorOutputLayer): Promise<ImageRef> {
    const formData = new FormData()
    formData.append('image', layer.blob, layer.ref.filename)
    formData.append('type', 'input')
    formData.append('subfolder', 'clipspace')

    const response = await api.fetchApi('/upload/image', {
      method: 'POST',
      body: formData
    })

    if (!response.ok) {
      throw new Error(`Failed to upload image: ${layer.ref.filename}`)
    }

    try {
      const data = await response.json()
      if (data?.name) {
        return imageRefFromUploadResponse(data, layer.ref)
      }
    } catch (error) {
      console.warn('[MaskEditorSaver] Failed to parse upload response:', error)
    }

    return layer.ref
  }

  async function updateNodePreview(
    node: LGraphNode,
    outputData: EditorOutputData
  ): Promise<void> {
    const canvas = outputData.image.canvas
    const dataUrl = canvas.toDataURL('image/png')

    const mainImg = await loadImageFromUrl(dataUrl)
    node.imgs = [mainImg]

    app.canvas.setDirty(true)
  }

  function updateNodeWithServerReferences(
    node: LGraphNode,
    outputData: EditorOutputData
  ): void {
    const mainRef = outputData.image.ref

    node.images = [mainRef]

    const imageWidget = node.widgets?.find((w) => w.name === 'image')
    if (imageWidget) {
      // Widget value format differs between Cloud and OSS:
      // - Cloud: JUST the filename (subfolder handled by backend)
      // - OSS: subfolder/filename (traditional format)
      let widgetValue: string
      if (isCloud) {
        widgetValue =
          mainRef.filename + (mainRef.type ? ` [${mainRef.type}]` : '')
      } else {
        widgetValue = createAnnotatedPath(mainRef.filename, {
          subfolder: mainRef.subfolder,
          rootFolder: mainRef.type
        })
      }

      imageWidget.value = widgetValue

      if (node.properties) {
        node.properties['image'] = widgetValue
      }

      if (node.widgets_values && node.widgets) {
        const widgetIndex = node.widgets.indexOf(imageWidget)
        if (widgetIndex >= 0) {
          node.widgets_values[widgetIndex] = widgetValue
        }
      }
    }

    node.imgs = undefined
    const annotatedPath = createAnnotatedPath(mainRef.filename, {
      subfolder: mainRef.subfolder,
      rootFolder: mainRef.type
    })
    nodeOutputStore.setNodeOutputs(node, annotatedPath, { folder: 'input' })
    node.graph?.setDirtyCanvas(true)
  }

  function loadImageFromUrl(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'

      img.onload = () => resolve(img)
      img.onerror = (error) => {
        console.error('[MaskEditorSaver] Failed to load image:', url, error)
        reject(new Error(`Failed to load image: ${url}`))
      }

      img.src = url
    })
  }

  function createFileRef(filename: string): ImageRef {
    return {
      filename,
      subfolder: 'clipspace',
      type: 'input'
    }
  }

  return {
    save
  }
}
